import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, CheckCircle, LogIn } from "lucide-react";
import kestioLogoWhite from "../assets/kestio-logo-white.png";
import { PrivacyPolicyLink } from "../components/PrivacyPolicyLink";
import { authApi } from "../lib/api";
import { useTranslation, Trans } from "react-i18next";
import { changeLanguage } from "../i18n";

interface AuthPageProps {
  onLogin: (user: any) => void;
}

export function AuthPage({ onLogin }: AuthPageProps) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [allowTestLogin, setAllowTestLogin] = useState(false);

  useEffect(() => {
    fetch("/api/config").then(r => r.json()).then(c => {
      if (c.allowTestLogin) setAllowTestLogin(true);
    }).catch(() => {});
  }, []);

  const validateEmail = (value: string) => {
    if (!value) { setEmailError(null); return; }
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    setEmailError(valid ? null : (i18n.language === "fr" ? "Adresse email invalide" : "Invalid email address"));
  };

  const handleTestLogin = async () => {
    if (!email) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/test-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        credentials: "same-origin",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Erreur de connexion");
      // test-login sets the cookie; fetch the full user profile via /me
      const meRes = await fetch("/api/auth/me", { credentials: "same-origin" });
      const meJson = await meRes.json();
      if (!meRes.ok) throw new Error(meJson.error || "Erreur de connexion");
      onLogin(meJson.user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await authApi.requestMagicLink(email);
      setSent(true);
    } catch (err: any) {
      setError(err.message || t("auth.sendError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <button
        type="button"
        onClick={() => changeLanguage(i18n.language === "fr" ? "en" : "fr")}
        className="absolute top-4 right-4 flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
        title={i18n.language === "fr" ? "Switch to English" : "Passer en français"}
        aria-label={i18n.language === "fr" ? "Switch to English" : "Passer en français"}
      >
        <span className="text-base leading-none">{i18n.language === "fr" ? "🇬🇧" : "🇫🇷"}</span>
        <span>{i18n.language === "fr" ? "English" : "Français"}</span>
      </button>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-end justify-center gap-3 mb-4 pb-1">
            <img src={kestioLogoWhite} alt="Kestio" className="h-8 invert" />
            <span
              className="font-bold text-2xl relative"
              style={{ fontFamily: 'Sora, sans-serif', color: '#384872', top: '8px' }}
            >
              BizCoach
            </span>
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Sora, sans-serif' }}>
            {sent ? t("auth.emailSent") : t("auth.connection")}
          </CardTitle>
          <CardDescription>
            {sent
              ? t("auth.checkInbox")
              : t("auth.enterEmail")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <p className="text-sm text-muted-foreground">
                <Trans i18nKey="auth.linkSentTo" values={{ email }}>
                  Un lien de connexion a été envoyé à <strong>{{ email } as any}</strong>.
                </Trans>
                <br />
                {t("auth.expiresIn15")}
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  // On conserve l'email saisi : le client dont le lien a expiré
                  // revient au formulaire pré-rempli et n'a plus qu'à recliquer
                  // « Recevoir un lien de connexion » (pas besoin de retaper).
                  setSent(false);
                }}
              >
                {t("auth.useOtherAddress")}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t("auth.emailPlaceholder")}
                    className={`pl-10 ${emailError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailError) validateEmail(e.target.value); }}
                    onBlur={(e) => validateEmail(e.target.value)}
                    required
                  />
                </div>
                {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? t("auth.sending") : t("auth.receiveMagicLink")}
              </Button>
              {allowTestLogin && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleTestLogin}
                  disabled={isLoading || !email}
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Connexion directe (test)
                </Button>
              )}
            </form>
          )}
          {/* Politique de confidentialité : mise à disposition pour information (13/10/2026). */}
          <PrivacyPolicyLink className="mt-4 text-center text-xs text-muted-foreground" />
        </CardContent>
      </Card>
    </div>
  );
}
